/**
 * Browser worker for server-driven Collect&Go sync.
 * Only runs after explicit start/resume; pauses on unload.
 */

import {
  cogoCommand,
  probeCogoExtensionFast,
  withCogoTabLock,
} from './cogoExtension'
import {
  pushSyncSnapshot,
  hideSyncHud,
  showSyncEndHudThenHide,
  type SyncHudPayload,
} from './cogoSyncHud'
import { registerSyncRunner, unregisterSyncRunner } from './cogoSyncRunners'

export type CogoSyncSnapshot = {
  orderId: number
  status:
    | 'running'
    | 'paused'
    | 'waiting_login'
    | 'waiting_choice'
    | 'done'
    | 'error'
  phase: string
  itemIndex: number
  itemCount: number
  currentProductId?: number
  currentDisplay?: string
  log: Array<{
    id: number
    at: string
    message: string
    kind?: string
  }>
  pendingCmdType: string | null
  lastError: string | null
  items: Array<{
    productId: number
    quantity: number
    display: string
    unit: string
    status: string
    messageAgent?: string
    technicalProductId?: string
    unitsAdded?: number
  }>
  updatedAt: string
  cartRevision: number
  workerConnected: boolean
}

type SyncCmd = {
  id: string
  type: string
  url?: string
  active?: boolean
  quantity?: number
  quantityUnit?: 'st' | 'kg'
  name?: string
  title?: string
  assortmentUrl?: string
  searchTerm?: string
  ms?: number
  unitType?: 'P' | 'W' | 'PA' | null
}

function cmdToHud(cmd: SyncCmd | null): SyncHudPayload['pendingCmd'] {
  if (!cmd) return null
  return {
    type: cmd.type,
    quantity: cmd.quantity,
    quantityUnit: cmd.quantityUnit,
    unitType: cmd.unitType ?? null,
    title: cmd.title,
    assortmentUrl: cmd.assortmentUrl,
    url: cmd.url,
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

async function execCmd(cmd: SyncCmd): Promise<Record<string, unknown>> {
  if (cmd.type === 'delay') {
    await sleep(Math.max(0, Number(cmd.ms) || 0))
    return { ok: true }
  }
  const result = await cogoCommand(
    {
      type: cmd.type,
      url: cmd.url,
      active: cmd.active,
      quantity: cmd.quantity,
      quantityUnit: cmd.quantityUnit,
      name: cmd.name,
      title: cmd.title,
      assortmentUrl: cmd.assortmentUrl,
      searchTerm: cmd.searchTerm,
      unitType: cmd.unitType,
    },
    cmd.type === 'navigate' || cmd.type === 'ensureTab' ? 90_000 : 60_000
  )
  return result as Record<string, unknown>
}

export async function fetchCogoSyncSnapshot(
  orderId: number
): Promise<CogoSyncSnapshot | null> {
  const res = await fetch(`/api/orders/${orderId}/cogo-sync`, {
    credentials: 'include',
  })
  if (!res.ok) return null
  const data = (await res.json()) as { job: CogoSyncSnapshot | null }
  return data.job
}

export async function startCogoSync(
  orderId: number,
  items: Array<{
    productId: number
    quantity: number
    display: string
    unit: string
    messageHuman?: string
  }>,
  workerId: string,
  opts?: { replace?: boolean }
): Promise<CogoSyncSnapshot> {
  const res = await fetch(`/api/orders/${orderId}/cogo-sync`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items,
      workerId,
      replace: opts?.replace === true,
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.message || 'Sync starten mislukt')
  return data.job as CogoSyncSnapshot
}

export async function pauseCogoSync(orderId: number): Promise<void> {
  await fetch(`/api/orders/${orderId}/cogo-sync/pause`, {
    method: 'POST',
    credentials: 'include',
  })
}

export async function resumeCogoSync(
  orderId: number,
  workerId: string
): Promise<CogoSyncSnapshot> {
  const res = await fetch(`/api/orders/${orderId}/cogo-sync/resume`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workerId }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.message || 'Sync hervatten mislukt')
  return data.job as CogoSyncSnapshot
}

export async function cancelCogoSync(orderId: number): Promise<void> {
  await fetch(`/api/orders/${orderId}/cogo-sync/cancel`, {
    method: 'POST',
    credentials: 'include',
  })
}

/**
 * Run the worker loop until paused/done/error or aborted.
 * Call only after start/resume on this client.
 */
export async function runCogoSyncWorker(opts: {
  orderId: number
  workerId: string
  signal?: AbortSignal
  onSnapshot?: (snap: CogoSyncSnapshot) => void
  /** Run N browser commands then auto-pause (for step-by-step debugging). */
  maxSteps?: number
}): Promise<void> {
  const { orderId, workerId, signal, maxSteps } = opts

  const ext = await probeCogoExtensionFast()
  if (!ext.installed) {
    throw new Error('Chrome-extensie niet actief')
  }

  const runnerAbort = new AbortController()
  const linked = new AbortController()
  const onParentAbort = () => linked.abort()
  const onRunnerAbort = () => linked.abort()
  signal?.addEventListener('abort', onParentAbort)
  runnerAbort.signal.addEventListener('abort', onRunnerAbort)
  registerSyncRunner(orderId, workerId, runnerAbort)

  let stepsDone = 0
  let noCmdPolls = 0

  try {
    await withCogoTabLock(`sync:${orderId}`, async () => {
      let cmd: SyncCmd | null = null

      while (!linked.signal.aborted) {
        const claimRes: Response = await fetch(
          `/api/orders/${orderId}/cogo-sync/claim`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ workerId }),
          }
        )
        const claimData: {
          message?: string
          job?: CogoSyncSnapshot
          cmd?: SyncCmd | null
        } = await claimRes.json().catch(() => ({}))
        if (!claimRes.ok) {
          throw new Error(claimData.message || 'Claim mislukt')
        }
        const snap = claimData.job
        if (!snap) {
          await sleep(400)
          continue
        }
        opts.onSnapshot?.(snap)
        cmd = claimData.cmd || null
        await pushSyncSnapshot(snap, cmdToHud(cmd))
        if (snap.status !== 'running') return

        if (!cmd) {
          if (maxSteps != null) {
            noCmdPolls += 1
            if (noCmdPolls > 25) return
          }
          await sleep(400)
          continue
        }
        noCmdPolls = 0

        let result: Record<string, unknown>
        try {
          result = await execCmd(cmd)
        } catch (e) {
          result = {
            ok: false,
            status: 'error',
            message: e instanceof Error ? e.message : String(e),
          }
        }

        if (linked.signal.aborted) return

        const applyRes: Response = await fetch(
          `/api/orders/${orderId}/cogo-sync/result`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workerId,
              cmdId: cmd.id,
              result,
            }),
          }
        )
        const applyData: {
          message?: string
          job?: CogoSyncSnapshot
          cmd?: SyncCmd | null
        } = await applyRes.json().catch(() => ({}))
        if (!applyRes.ok) {
          throw new Error(applyData.message || 'Resultaat doorgeven mislukt')
        }
        if (applyData.job) {
          opts.onSnapshot?.(applyData.job)
          await pushSyncSnapshot(
            applyData.job,
            cmdToHud(applyData.cmd || null)
          )
        }
        cmd = applyData.cmd || null

        const nextStatus = applyData.job?.status
        if (nextStatus && nextStatus !== 'running') return

        stepsDone += 1
        if (maxSteps != null && stepsDone >= maxSteps) {
          if (applyData.job?.status === 'running') {
            await pauseCogoSync(orderId)
            const pausedSnap = await fetchCogoSyncSnapshot(orderId)
            if (pausedSnap) {
              opts.onSnapshot?.(pausedSnap)
              await pushSyncSnapshot(
                pausedSnap,
                cmdToHud(applyData.cmd || null)
              )
            }
          }
          return
        }
      }
    })
  } finally {
    signal?.removeEventListener('abort', onParentAbort)
    runnerAbort.signal.removeEventListener('abort', onRunnerAbort)
    unregisterSyncRunner(orderId, workerId)
    const snap = await fetchCogoSyncSnapshot(orderId)
    if (
      snap?.status === 'paused' ||
      snap?.status === 'waiting_choice' ||
      snap?.status === 'waiting_login'
    ) {
      if (snap) await pushSyncSnapshot(snap)
    } else if (snap?.status === 'done' || snap?.status === 'error') {
      await showSyncEndHudThenHide(snap)
    } else {
      await hideSyncHud()
    }
  }
}

/** Best-effort pause when leaving the page. */
export function pauseCogoSyncBeacon(orderId: number) {
  try {
    const body = JSON.stringify({})
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(`/api/orders/${orderId}/cogo-sync/pause`, blob)
      return
    }
  } catch {
    /* ignore */
  }
  void pauseCogoSync(orderId)
}
